import { effect, inject, Injectable } from '@angular/core';
import { StorageRepository } from '@okcargo-apps/shared-domain';
import { AccountService } from '../account';

export interface IDecaDriverPlateCriteria {
  plate?: string;
  withoutPlate?: boolean;
}

interface IStoredDecaDriverPlateCriteria extends IDecaDriverPlateCriteria {
  ownerId: string;
}

const STORAGE_KEY = 'deca-driver-plate';

/**
 * @description
 * Stores the plate criterion selected by a driver, so it can be recovered the next time the driver
 * opens the app from the same browser. The stored criteria belong to a single contact: they are
 * deleted when another contact starts a session or when the current one logs out.
 */
@Injectable({
  providedIn: 'root',
})
export class DecaDriverPlateStorageService {
  private readonly storageService = inject(StorageRepository);
  private readonly accountService = inject(AccountService);

  private previousOwnerId: string | null = null;

  constructor() {
    // The criteria of the previous contact are useless once the identity changes: they are removed
    // on logout (the identity becomes empty) and when another contact starts a session.
    effect(() => {
      const ownerId = this.accountService.account().id;
      const previousOwnerId = this.previousOwnerId;
      this.previousOwnerId = ownerId;

      if (previousOwnerId && previousOwnerId !== ownerId) {
        void this.clear();
      }
    });
  }

  public async read(): Promise<IDecaDriverPlateCriteria | null> {
    const ownerId = this.accountService.account().id;
    if (!ownerId) return null;

    try {
      const { value } = await this.storageService.read(STORAGE_KEY);
      if (!value) return null;

      const stored = JSON.parse(value) as IStoredDecaDriverPlateCriteria;

      if (stored.ownerId !== ownerId) {
        await this.clear();
        return null;
      }

      return { plate: stored.plate, withoutPlate: stored.withoutPlate };
    } catch (error) {
      console.error('[DecaDriverPlateStorageService] Error reading the stored plate criteria:', error);
      return null;
    }
  }

  public async save(criteria: IDecaDriverPlateCriteria): Promise<void> {
    const ownerId = this.accountService.account().id;
    if (!ownerId) return;

    try {
      const stored: IStoredDecaDriverPlateCriteria = { ownerId, ...criteria };
      await this.storageService.update(STORAGE_KEY, JSON.stringify(stored));
    } catch (error) {
      console.error('[DecaDriverPlateStorageService] Error saving the plate criteria:', error);
    }
  }

  public async clear(): Promise<void> {
    try {
      await this.storageService.delete(STORAGE_KEY);
    } catch (error) {
      console.error('[DecaDriverPlateStorageService] Error deleting the stored plate criteria:', error);
    }
  }
}
